import {
  mdiAccountBoxOutline,
  mdiAccountMultipleOutline,
  mdiApps,
  mdiCloudKeyOutline,
  mdiCodeTags,
  mdiEmailOutline,
  mdiFileDocumentOutline,
  mdiFinance,
  mdiHomeVariantOutline,
  mdiLifebuoy,
  mdiShieldLockOutline,
  mdiSitemapOutline,
  mdiStore,
  mdiWebPlus,
} from '@mdi/js';
import {
  auditLogsPath,
  customHostnamesPath,
  invitesPath,
  membersPath,
  mfaPath,
  ssoPath,
  supportAccessPath,
  usagePath,
  userGroupListPath,
} from 'features/admin/components/admin-paths';
import { useAuth0TokenClaims } from 'features/common/auth/claims';
import { useAppAccess } from 'features/common/components/OrganizationAppAccess';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useFeatures } from 'lib/featureFlags/features';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { contextIdsRoute } from '../../../context-ids/routes';
import { useGetTenantsByOrgAndProductCode } from '../../services/inventory/tenants';
import { fetchLastSuccessfulLoginOrganizationId } from '../../auth/auth0Storage';

const getIcon = (path: string | undefined) => <PortalIcon path={path} />;

// Helper function to get search params without appId for non-developer routes
const getSearchParamsWithoutAppId = (search: string) => {
  const searchParams = new URLSearchParams(search);
  searchParams.delete('appId');
  return searchParams.toString() ? `?${searchParams.toString()}` : '';
};

const useGetMainMenuItems = () => {
  const { hasOrgOwnerAccess, hasOrgAdminAccess } = useAppAccess();
  const features = useFeatures();
  const organizationId = fetchLastSuccessfulLoginOrganizationId() ?? '';
  const { data } = useGetTenantsByOrgAndProductCode({
    organizationId,
    productCode: 'mkpdevelopers',
  });
  const location = useLocation();

  const mainMenuItems = useMemo(() => {
    const searchParams = getSearchParamsWithoutAppId(location.search);

    return [
      {
        label: 'Home',
        href: `/${searchParams}`,
        active: location.pathname === '/',
        valid: true,
        mobileMenuIcon: getIcon(mdiHomeVariantOutline),
      },
      {
        label: 'Marketplace',
        href: `/marketplace${searchParams}`,
        active: location.pathname.startsWith('/marketplace'),
        valid: true,
        mobileMenuIcon: getIcon(mdiStore),
      },
      {
        label: 'My apps',
        href: `/my-apps${searchParams}`,
        active: location.pathname.startsWith('/my-apps'),
        valid:
          (data?.totalCount ?? 0) > 0 &&
          (hasOrgOwnerAccess || hasOrgAdminAccess),
        mobileMenuIcon: getIcon(mdiApps),
      },
      {
        label: 'Unified Tracking',
        href: `/unified-tracking${searchParams}`,
        active: location.pathname.startsWith('/unified-tracking'),
        valid: features.UNIFIED_TRACKING,
        mobileMenuIcon: getIcon(mdiSitemapOutline),
      },
      {
        label: 'App studio',
        href: `/app-studio${searchParams}`,
        active: location.pathname.startsWith('/app-studio'),
        valid:
          (data?.totalCount ?? 0) > 0 &&
          (hasOrgOwnerAccess || hasOrgAdminAccess),
        mobileMenuIcon: getIcon(mdiCodeTags),
      },
      {
        label: 'Admin',
        href: `/admin/members/page/1${searchParams}`,
        active: location.pathname.startsWith('/admin'),
        valid: hasOrgOwnerAccess || hasOrgAdminAccess,
      },
      // {
      //   label: 'Subscriptions',
      //   href: `${subscriptionsRoute}${location.search}`,
      //   active: location.pathname.startsWith(subscriptionsRoute),
      //   valid: Boolean(features.SUBSCRIPTIONS && hasOrgOwnerAccess),
      //   mobileMenuIcon: getIcon(mdiWalletOutline),
      // },
    ].filter((item) => item.valid);
  }, [
    features.UNIFIED_TRACKING,
    hasOrgAdminAccess,
    hasOrgOwnerAccess,
    location.pathname,
    location.search,
    data?.totalCount,
  ]);

  return { mainMenuItems };
};

const useGetUserManagementMenuItems = () => {
  const features = useFeatures();
  const { hasOrgOwnerAccess, hasOrgAdminAccess } = useAppAccess();
  const location = useLocation();

  const userManagementMenuItems = useMemo(() => {
    const searchParams = getSearchParamsWithoutAppId(location.search);

    return [
      {
        label: 'Users',
        href: `/admin${membersPath}${searchParams}`,
        active:
          location.pathname.startsWith(`/admin${membersPath}/page/`) ||
          location.pathname.startsWith(`/admin${membersPath}/configuration`),
        icon: getIcon(mdiAccountMultipleOutline),
        enabled: hasOrgOwnerAccess || hasOrgAdminAccess,
      },
      {
        label: 'User groups',
        href: `/admin${userGroupListPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${userGroupListPath}`),
        icon: getIcon(mdiAccountBoxOutline),
        enabled:
          features.USER_GROUPS && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
      {
        label: 'Invitations',
        href: `/admin${invitesPath}/page/1${searchParams}`,
        active: location.pathname.startsWith(`/admin${invitesPath}/page/`),
        icon: getIcon(mdiEmailOutline),
        enabled:
          features.INVITATIONS && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
    ].filter((item) => item.enabled);
  }, [
    features,
    hasOrgAdminAccess,
    hasOrgOwnerAccess,
    location.pathname,
    location.search,
  ]);

  return { userManagementMenuItems };
};

const useGetorganizationManagementMenuItems = () => {
  const features = useFeatures();
  const { hasOrgOwnerAccess, hasOrgAdminAccess } = useAppAccess();
  const { claims } = useAuth0TokenClaims();
  const location = useLocation();

  const organizationManagementMenuItems = useMemo(() => {
    const searchParams = getSearchParamsWithoutAppId(location.search);

    return [
      {
        label: 'Usage',
        href: `/admin${usagePath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${usagePath}`),
        icon: getIcon(mdiFinance),
        enabled: features.USAGE_TRACKING && hasOrgOwnerAccess,
      },
      {
        label: 'Multi-factor authentication',
        href: `/admin${mfaPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${mfaPath}`),
        icon: getIcon(mdiShieldLockOutline),
        enabled: features.MFA && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
      {
        label: 'Single sign-on (SSO)',
        href: `/admin${ssoPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${ssoPath}`),
        icon: getIcon(mdiCloudKeyOutline),
        enabled: features.SSO && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
      {
        label: 'Sitecore Support access',
        href: `/admin${supportAccessPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${supportAccessPath}`),
        icon: getIcon(mdiLifebuoy),
        enabled:
          features.SUPPORT_ACCESS &&
          (hasOrgOwnerAccess || hasOrgAdminAccess || claims?.sc_support_login),
      },
      {
        label: 'Custom hostnames',
        href: `/admin${customHostnamesPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${customHostnamesPath}`),
        icon: getIcon(mdiWebPlus),
        enabled:
          features.CUSTOM_HOSTNAMES && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
      {
        label: 'Audit logs',
        href: `/admin${auditLogsPath}${searchParams}`,
        active: location.pathname.startsWith(`/admin${auditLogsPath}`),
        icon: getIcon(mdiFileDocumentOutline),
        enabled:
          features.AUDIT_LOGS && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
    ].filter((item) => item.enabled);
  }, [
    features,
    hasOrgAdminAccess,
    hasOrgOwnerAccess,
    claims?.sc_support_login,
    location.pathname,
    location.search,
  ]);

  return { organizationManagementMenuItems };
};

const useGetIdManagementMenuItems = () => {
  const features = useFeatures();
  const { hasOrgOwnerAccess, hasOrgAdminAccess } = useAppAccess();
  const location = useLocation();

  const idManagementMenuItems = useMemo(() => {
    const searchParams = getSearchParamsWithoutAppId(location.search);

    return [
      {
        label: 'Context IDs',
        href: `${contextIdsRoute}${searchParams}`,
        active: location.pathname.startsWith(contextIdsRoute),
        icon: getIcon(mdiSitemapOutline),
        enabled:
          features.CONTEXT_MAPPING && (hasOrgOwnerAccess || hasOrgAdminAccess),
      },
    ].filter((item) => item.enabled);
  }, [
    features,
    hasOrgAdminAccess,
    hasOrgOwnerAccess,
    location.pathname,
    location.search,
  ]);

  return { idManagementMenuItems };
};

export {
  useGetIdManagementMenuItems,
  useGetMainMenuItems,
  useGetorganizationManagementMenuItems,
  useGetUserManagementMenuItems,
};
